import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { opportunityStats } from '../data/quotation';
const OUTCOMES = ['Increased healthcare professional engagement with existing brand content', 'Rich qualitative and quantitative data for continuous optimisation', 'Campaign turnarounds in under a week', 'Improved website engagement and return visitor rates', 'Consistent, scalable campaign delivery across every market', 'A proven playbook for expanding to more countries and more brands'];
const PROBLEMS = [{
  title: 'Low margins, no agency budget',
  body: 'Established brands rarely clear the threshold that justifies traditional agency fees, so they receive little marketing investment or attention despite carrying the revenue.'
}, {
  title: 'Content exists, engagement does not',
  body: 'The science is already written. It is long, dense and rarely read by time-poor healthcare professionals, and it generates almost no usable engagement data.'
}, {
  title: 'Tactical, not strategic, deployment',
  body: 'Activity is opportunistic and invisible globally. Without clear intent about which brands, campaigns and markets to prioritise, nothing compounds into portfolio-level learning.'
}];
export function OpportunitySection() {
  return <section id="opportunity" className="border-t border-line bg-white py-20 sm:py-28" data-id="element-117" data-mp="2wwg0wp5f44gu">
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8" data-id="element-118" data-mp="2wulhjf9sokev">
        <SectionHeading eyebrow="The opportunity" title="A 1% shift on the established portfolio is worth more than a new launch" description="Between 30 and 35% of big pharma revenue comes from legacy products, and Leo Pharma's portfolio is weighted further still, with 76 to 85% sitting in established and core brands. Even a single percentage point of improvement is substantial upside, and it is addressable without creating a single new piece of content." data-id="element-119" data-mp="31u7q3beohchc" />

        <dl className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4" data-id="element-120" data-mp="3j4bpmk3jwux4">
          {opportunityStats.map((stat, index) => <motion.div key={stat.value} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4,
          delay: index * 0.06
        }} className="bg-surface p-6" data-id="element-121" data-mp="39e0b63m8r3bz" data-mpi={index}>
              <dt className="font-display text-3xl font-bold tracking-tight text-primary" data-id="element-122" data-mp="2vdzaop9w1xx1">
                {stat.value}
              </dt>
              <dd className="mt-3 text-sm leading-relaxed text-ink-500" data-id="element-123" data-mp="2lhcr03tatcwl">
                {stat.label}
              </dd>
            </motion.div>)}
        </dl>

        <div className="mt-16 grid gap-6 lg:grid-cols-3" data-id="element-124" data-mp="omqbisp0zcym">
          {PROBLEMS.map(problem => <article key={problem.title} className="rounded-2xl border border-line bg-white p-6" data-id="element-125" data-mp="jnn4j2phiwo0">
              <h3 className="font-display text-lg font-semibold text-ink-900" data-id="element-126" data-mp="w7tuj43crhcg">
                {problem.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-500" data-id="element-127" data-mp="110ddkgkt74on">
                {problem.body}
              </p>
            </article>)}
        </div>

        <div className="mt-10 rounded-2xl bg-primary p-8 sm:p-10" data-id="element-128" data-mp="8awjlehjjfqb">
          <h3 className="font-display text-xl font-semibold tracking-tight text-white" data-id="element-129" data-mp="8cr8t4zuhu07">
            What this programme is expected to deliver
          </h3>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-130" data-mp="i7rk22xdjq78">
            {OUTCOMES.map(outcome => <li key={outcome} className="flex items-start gap-2.5 text-sm leading-relaxed text-white/90" data-id="element-131" data-mp="n8yqnyns6jcb">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-132" data-mp="1ym17mutslax0" />
                {outcome}
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}